// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignCreate.jsx
// Название: Create Campaign Page - Compact Design
// Версия: 5.0.0
// Дата: 28.12.2025

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import './CampaignCreate.css';

const CampaignCreate = ({ campaignType }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const typeConfig = {
    b2b: {
      title: '🎯 Новая B2B кампания',
      subtitle: 'Персональные AI-подарки для сотрудников',
      backPath: '/business/b2b/campaigns',
      campaignTypeValue: 'b2b'
    },
    brand: {
      title: '🎁 Новая Brand кампания',
      subtitle: 'Привлечение и удержание клиентов',
      backPath: '/business/brand/campaigns',
      campaignTypeValue: 'brand_referral'
    }
  };

  const config = typeConfig[campaignType] || typeConfig.b2b;

  const [form, setForm] = useState({
    name: '',
    occasion: 'gift',
    amount: 50,
    expires_days: 10,
    brand_subtype: 'brand_referral'
  });

// Все поводы - 2 ряда по 6
  const occasions = [
    { id: 'gift', emoji: '🎁', label: 'Подарок' },
    { id: 'birthday', emoji: '🎂', label: 'День рождения' },
    { id: 'newyear', emoji: '🎆', label: 'Новый год' },
    { id: 'christmas', emoji: '🎄', label: 'Рождество' },
    { id: 'bonus', emoji: '💰', label: 'Бонус' },
    { id: 'thankyou', emoji: '🙏', label: 'Спасибо' },
    { id: 'welcome', emoji: '👋', label: 'Welcome' },
    { id: 'achievement', emoji: '🏆', label: 'Достижение' },
    { id: 'corporate', emoji: '🏢', label: 'Корпоратив' },
    { id: 'employee', emoji: '👔', label: 'Сотруднику' },
    { id: 'partner', emoji: '🤝', label: 'Партнёру' },
    { id: 'loyalty', emoji: '⭐', label: 'Лояльность' }
  ];

  const brandSubtypes = [
    { id: 'brand_referral', emoji: '🔗', label: 'Реферальная', desc: 'Приведи друга' },
    { id: 'brand_influencer', emoji: '⭐', label: 'Инфлюенсер', desc: 'Через лидеров' },
    { id: 'brand_p2p', emoji: '🤝', label: 'P2P', desc: 'Вирусное' }
  ];

  const amounts = [25, 50, 100, 200];

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('Введите название кампании');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let finalCampaignType = config.campaignTypeValue;
      if (campaignType === 'brand') {
        finalCampaignType = form.brand_subtype;
      }

      const response = await api.post('/campaigns', {
        name: form.name,
        description: form.occasion,
        budget_dar: form.amount,
        reward_amount: form.amount,
        campaign_type: finalCampaignType,
        b2b_config: campaignType === 'b2b' ? { event_type: form.occasion } : null,
        brand_config: campaignType === 'brand' ? { subtype: form.brand_subtype } : null
      });

      navigate('/business/campaigns/' + response.data.id + '/flux');
    } catch (err) {
      setError(err.message || 'Ошибка создания');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="campaign-create-compact">
      <div className="create-card">
        <div className="create-header">
          <h1>{config.title}</h1>
          <p>{config.subtitle}</p>
        </div>

        <div className="create-body">
          {/* Название */}
          <div className="field-group">
            <label>Название кампании</label>
            <input
              type="text"
              placeholder="Например: Новогодние подарки 2025"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </div>

          {/* Brand: тип кампании */}
          {campaignType === 'brand' && (
            <div className="field-group">
              <label>Тип кампании</label>
              <div className="brand-types">
                {brandSubtypes.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={`brand-type-btn ${form.brand_subtype === t.id ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, brand_subtype: t.id })}
                  >
                    <span className="emoji">{t.emoji}</span>
                    <span className="label">{t.label}</span>
                    <span className="desc">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Повод */}
          <div className="field-group">
            <label>Повод</label>
            <div className="occasions-grid">
              {occasions.map(o => (
                <button
                  key={o.id}
                  type="button"
                  className={`occasion-btn ${form.occasion === o.id ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, occasion: o.id })}
                >
                  <span>{o.emoji}</span>
                  <span>{o.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Сумма и Срок в одной строке */}
          <div className="field-row">
            <div className="field-group">
              <label>Сумма (DAR)</label>
              <div className="amount-btns">
                {amounts.map(a => (
                  <button
                    key={a}
                    type="button"
                    className={`amount-btn ${form.amount === a ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, amount: a })}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div className="field-group">
              <label>Срок действия</label>
              <div className="expire-btns">
                {[7, 14, 30].map(d => (
                  <button
                    key={d}
                    type="button"
                    className={`expire-btn ${form.expires_days === d ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, expires_days: d })}
                  >
                    {d} дн
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && <div className="error-msg">❌ {error}</div>}

          {/* Кнопки */}
          <div className="create-actions">
            <button className="btn-cancel" onClick={() => navigate(config.backPath)}>
              Отмена
            </button>
            <button
              className="btn-next"
              onClick={handleSubmit}
              disabled={loading || !form.name.trim()}
            >
              {loading ? 'Создание...' : 'Далее: выбор получателей →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCreate;